import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Check, Gavel, AlertTriangle } from 'lucide-react';
import {
  Field, Messages, Table, asList, capabilitiesAllow, decimalProps, displayDec, errorMessage, formatWhen,
  isDecimal, isPositiveDecimal, money, scopeKey, shortId, statusLabel, useCtlRuntime, useCtlScope, useStableIds,
} from './ControlsShared';
import './Controls.css';

const TABS = [['comp', 'Comp allowances'], ['centres', 'Cost centres'], ['consumables', 'Consumables'], ['exceptions', 'Exceptions']];
const KIND_HINTS = [
  { value: '', label: 'No hint' },
  { value: 'partner', label: 'Partner' },
  { value: 'staff', label: 'Staff' },
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'other', label: 'Other' },
];
const DECISIONS = [{ value: 'invoice', label: 'Invoice the counterparty' }, { value: 'writeoff', label: 'Write off' }];
const CATEGORIES = [
  { value: 'consumables', label: 'Consumables' },
  { value: 'chemicals', label: 'Chemicals' },
  { value: 'smallwares', label: 'Smallwares' },
  { value: 'comp_meals', label: 'Comp meals' },
];
const CENTRE_SCOPES = [{ value: 'org', label: 'Whole organisation' }, { value: 'site', label: 'Current site only' }];
const EXCEPTION_FILTERS = [
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
  { value: '', label: 'All' },
];
const DECIDABLE = ['raised', 'notified'];
const RESOLVABLE = ['open', 'acknowledged'];
const PERIOD = /^\d{4}-(0[1-9]|1[0-2])$/;
const enc = encodeURIComponent;
const currentPeriod = () => new Date().toISOString().slice(0, 7);
const blankComp = { amount: '', reason: '', guest: '', counterparty: '', kind: '' };
const blankIssue = { centre: '', item: '', qty: '', uom: '', notes: '' };
const blankCount = { centre: '', item: '', qty: '', uom: '', period: currentPeriod() };

const SIGNED_DECIMAL = /^-?\d+(\.\d+)?$/;
const signedDec = v => {
  const text = typeof v === 'string' ? v.trim() : '';
  return SIGNED_DECIMAL.test(text) ? `${text.startsWith('-') ? '-' : ''}${displayDec(text.replace(/^-/, ''), 0)}` : '';
};

/**
 * Turns an issue/count response into human hints. The backend reports variance either as a
 * ready-made string or as an object of exact decimal strings; anything absent is simply omitted.
 */
export function varianceHints(result) {
  if (!result || typeof result !== 'object') return [];
  const hints = [];
  const variance = result.variance ?? result.variance_hint;
  if (typeof variance === 'string' && variance.trim()) hints.push(variance.trim());
  else if (variance && typeof variance === 'object') {
    const uom = variance.uom ? ` ${variance.uom}` : '';
    const parts = [];
    const add = (key, render) => { const v = signedDec(variance[key]); if (v) parts.push(render(v)); };
    add('expected_qty', v => `expected ${v}${uom}`);
    add('counted_qty', v => `counted ${v}${uom}`);
    add('variance_qty', v => `variance ${v}${uom}`);
    add('variance_pct', v => `(${v}%)`);
    if (parts.length) hints.push(`Variance: ${parts.join(', ')}.`);
  }
  if (result.exception_id) hints.push(`This variance raised exception ${shortId(result.exception_id)} for review.`);
  return hints;
}

export default function Controls() {
  const scope = useCtlScope();
  return <ControlsWorkspace key={scopeKey(scope)} scope={scope} />;
}

export function ControlsWorkspace({ scope }) {
  const { active, request, run, busy, error, notice, setError, setNotice } = useCtlRuntime(scope);
  const { idFor, release } = useStableIds();
  const [caps, setCaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState('');
  const [tab, setTab] = useState('comp');
  const [listLoading, setListLoading] = useState(false);
  const [comps, setComps] = useState([]);
  const [centres, setCentres] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [exceptionFilter, setExceptionFilter] = useState('open');
  const [compForm, setCompForm] = useState(blankComp);
  const [deciding, setDeciding] = useState(null);
  const [decision, setDecision] = useState({ decision: 'invoice', note: '' });
  const [centreForm, setCentreForm] = useState({ name: '', category: 'consumables', scope: 'org' });
  const [issueForm, setIssueForm] = useState(blankIssue);
  const [countForm, setCountForm] = useState(blankCount);
  const [hints, setHints] = useState([]);
  const [resolving, setResolving] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const canOperate = Boolean(caps?.can_operate);
  const canWrite = Boolean(caps?.can_write);
  const hasSite = Boolean(scope.locationId);

  const loadCaps = useCallback(async () => {
    setLoading(true); setBlocked(''); setError('');
    try {
      if (!scope.orgId || !scope.token) throw new Error('Sign in and select an organization to use Controls.');
      const c = await request('get', '/capabilities');
      if (!active()) return;
      if (!capabilitiesAllow(c)) { setCaps(null); setBlocked('Controls is not enabled for this organization, or you do not have access.'); return; }
      setCaps(c);
    } catch (e) { if (active()) { setCaps(null); setBlocked(errorMessage(e)); } }
    finally { if (active()) setLoading(false); }
  }, [scope, request, active, setError]);
  useEffect(() => { loadCaps(); }, [loadCaps]);

  const loadList = useCallback(async (path, apply, params) => {
    setListLoading(true);
    try {
      const data = await request('get', path, params ? { params } : undefined);
      if (active()) apply(asList(data));
    } catch (e) { if (active()) { apply([]); setError(errorMessage(e)); } }
    finally { if (active()) setListLoading(false); }
  }, [request, active, setError]);
  const loadComps = useCallback(() => loadList('/comp-allowances', setComps), [loadList]);
  const loadCentres = useCallback(() => loadList('/cost-centres', setCentres), [loadList]);
  const loadExceptions = useCallback(
    () => loadList('/exceptions', setExceptions, exceptionFilter ? { status: exceptionFilter } : undefined),
    [loadList, exceptionFilter],
  );

  useEffect(() => {
    if (!caps) return;
    if (tab === 'comp') loadComps();
    else if (tab === 'centres' || tab === 'consumables') loadCentres();
    else if (tab === 'exceptions') loadExceptions();
  }, [caps, tab, loadComps, loadCentres, loadExceptions]);

  const refresh = () => {
    loadCaps();
    if (tab === 'comp') loadComps();
    else if (tab === 'centres' || tab === 'consumables') loadCentres();
    else loadExceptions();
  };
  const switchTab = key => { setTab(key); setNotice(''); setError(''); setHints([]); };

  const raiseComp = async () => {
    const amount = compForm.amount.trim();
    const reason = compForm.reason.trim();
    if (!hasSite) { setError('Select a site before raising a comp allowance.'); return; }
    if (!isPositiveDecimal(amount)) { setError('Enter a comp amount greater than zero, for example 12.50.'); return; }
    if (!reason) { setError('Give a reason for the comp.'); return; }
    if (reason.length > 500) { setError('Keep the reason to 500 characters or fewer.'); return; }
    const body = {
      amount, reason,
      ...(compForm.guest.trim() ? { guest_name: compForm.guest.trim() } : {}),
      ...(compForm.counterparty.trim() ? { counterparty: compForm.counterparty.trim() } : {}),
      ...(compForm.kind ? { kind_hint: compForm.kind } : {}),
    };
    const r = await run(() => request('post', '/comp-allowances', { body }));
    if (!r?.ok || !active()) return;
    setCompForm(blankComp);
    setNotice(r.result?.over_cap ? 'Comp allowance raised. It is over the monthly cap and an exception was opened.' : 'Comp allowance raised.');
    loadComps();
  };

  const decideComp = async allowance => {
    const note = decision.note.trim();
    const fingerprint = ['decide', allowance.allowance_id, decision.decision, note];
    const body = { command_id: idFor(fingerprint), decision: decision.decision, ...(note ? { note } : {}) };
    const r = await run(() => request('post', `/comp-allowances/${enc(allowance.allowance_id)}/decide`, { body }));
    if (!r?.ok || !active()) return;
    release(fingerprint);
    setDeciding(null); setDecision({ decision: 'invoice', note: '' });
    setNotice(decision.decision === 'invoice' ? 'Comp allowance will be invoiced.' : 'Comp allowance written off.');
    loadComps();
  };

  const createCentre = async () => {
    const name = centreForm.name.trim();
    if (!name) { setError('Give the cost centre a name.'); return; }
    if (centreForm.scope === 'site' && !hasSite) { setError('Select a site to limit a cost centre to it.'); return; }
    const body = { name, category: centreForm.category, ...(centreForm.scope === 'site' ? { location_id: scope.locationId } : {}) };
    const r = await run(() => request('post', '/cost-centres', { body }));
    if (!r?.ok || !active()) return;
    setCentreForm({ name: '', category: centreForm.category, scope: centreForm.scope });
    setNotice('Cost centre created.');
    loadCentres();
  };

  const issueConsumable = async () => {
    const { centre, item, qty, uom, notes } = issueForm;
    if (!hasSite) { setError('Select a site before issuing consumables.'); return; }
    if (!centre) { setError('Choose a cost centre to issue to.'); return; }
    if (!item.trim()) { setError('Enter the item ID being issued.'); return; }
    if (!isPositiveDecimal(qty)) { setError('Issue quantity must be greater than zero.'); return; }
    if (!uom.trim()) { setError('Enter the unit of measure, for example kg.'); return; }
    const body = {
      cost_centre_id: centre, item_id: item.trim(), qty: qty.trim(), uom: uom.trim(),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    const r = await run(() => request('post', '/consumables/issues', { body }), 'Consumable issue recorded.');
    if (!r?.ok || !active()) return;
    setIssueForm({ ...blankIssue, centre });
    setHints(varianceHints(r.result));
  };

  const countConsumable = async () => {
    const { centre, item, qty, uom, period } = countForm;
    if (!hasSite) { setError('Select a site before recording a count.'); return; }
    if (!centre) { setError('Choose the cost centre being counted.'); return; }
    if (!item.trim()) { setError('Enter the item ID being counted.'); return; }
    if (!isDecimal(qty)) { setError('Counted quantity must be a number, for example 4.5. Use 0 for none left.'); return; }
    if (!uom.trim()) { setError('Enter the unit of measure, for example kg.'); return; }
    if (!PERIOD.test(period.trim())) { setError('Period must be a month in YYYY-MM format.'); return; }
    const body = { cost_centre_id: centre, item_id: item.trim(), qty: qty.trim(), uom: uom.trim(), period_key: period.trim() };
    const r = await run(() => request('post', '/consumables/counts', { body }), 'Count recorded.');
    if (!r?.ok || !active()) return;
    setCountForm({ ...blankCount, centre, period: period.trim() });
    setHints(varianceHints(r.result));
  };

  const acknowledge = async ticket => {
    const r = await run(() => request('post', `/exceptions/${enc(ticket.exception_id)}/acknowledge`), 'Exception acknowledged.');
    if (r?.ok && active()) loadExceptions();
  };

  const resolve = async ticket => {
    const note = resolveNote.trim();
    if (!note) { setError('Add a resolution note.'); return; }
    if (note.length > 500) { setError('Keep the resolution note to 500 characters or fewer.'); return; }
    const fingerprint = ['resolve', ticket.exception_id, note];
    const body = { command_id: idFor(fingerprint), note };
    const r = await run(() => request('post', `/exceptions/${enc(ticket.exception_id)}/resolve`, { body }), 'Exception resolved.');
    if (!r?.ok || !active()) return;
    release(fingerprint);
    setResolving(null); setResolveNote('');
    loadExceptions();
  };

  const centreOptions = [{ value: '', label: 'Choose cost centre' }, ...centres.map(c => ({ value: c.cost_centre_id, label: c.name }))];
  const set = (setter, form, key) => e => setter({ ...form, [key]: e.target.value });

  return (
    <main className="ctl-ops" data-testid="controls">
      <header>
        <h1>Controls</h1>
        <button type="button" title="Refresh" aria-label="Refresh controls" disabled={busy || loading} onClick={refresh}><RefreshCw size={18} /></button>
      </header>
      <Messages error={error} notice={notice} />
      {loading && <p role="status">Checking Controls access...</p>}
      {!loading && blocked && <div className="ctl-error" role="alert">{blocked}</div>}
      {!loading && caps && <>
        {!hasSite && <p className="ctl-hint" role="status">Select a site to raise comps, issue consumables and record counts.</p>}
        <div className="ctl-tabs" role="tablist" aria-label="Controls workflows">
          {TABS.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} disabled={busy} onClick={() => switchTab(key)}>{label}</button>
          ))}
        </div>

        {tab === 'comp' && (
          <section role="tabpanel" aria-label="Comp allowances">
            {canOperate && (
              <form className="ctl-form" aria-label="Raise comp allowance" onSubmit={e => { e.preventDefault(); raiseComp(); }}>
                <h2>Raise a comp</h2>
                <fieldset disabled={busy}>
                  <div className="ctl-fields">
                    <Field label="Comp amount" {...decimalProps} value={compForm.amount} onChange={set(setCompForm, compForm, 'amount')} />
                    <Field label="Comp reason" type="text" maxLength={500} value={compForm.reason} onChange={set(setCompForm, compForm, 'reason')} />
                    <Field label="Guest name" type="text" value={compForm.guest} onChange={set(setCompForm, compForm, 'guest')} />
                    <Field label="Counterparty" type="text" value={compForm.counterparty} onChange={set(setCompForm, compForm, 'counterparty')} />
                    <Field label="Kind hint" options={KIND_HINTS} value={compForm.kind} onChange={set(setCompForm, compForm, 'kind')} />
                  </div>
                  <button type="submit" className="ctl-primary" disabled={!hasSite}>Raise comp</button>
                </fieldset>
              </form>
            )}
            {listLoading ? <p role="status">Loading comp allowances...</p> : (
              <Table headers={['Raised', 'Amount', 'Guest', 'Reason', 'Kind', 'Status', 'Decide']} label="Comp allowances" empty={!comps.length} emptyText="No comp allowances yet.">
                {comps.map(a => (
                  <tr key={a.allowance_id}>
                    <td>{formatWhen(a.created_at)}</td>
                    <td className="ctl-number">{money(a.amount, a.currency)}</td>
                    <td>{a.guest_name || ''}{a.counterparty ? ` (${a.counterparty})` : ''}</td>
                    <td>{a.reason}</td>
                    <td>{statusLabel(a.kind)}</td>
                    <td>{statusLabel(a.status)}{a.over_cap && <span className="ctl-flag"> over cap</span>}</td>
                    <td>
                      {canWrite && DECIDABLE.includes(a.status) && (
                        <button type="button" aria-label={`Decide comp ${shortId(a.allowance_id)}`} disabled={busy}
                          onClick={() => { setDeciding(a.allowance_id); setDecision({ decision: 'invoice', note: '' }); }}><Gavel size={16} />Decide</button>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
            {canWrite && deciding && comps.filter(a => a.allowance_id === deciding && DECIDABLE.includes(a.status)).map(a => (
              <form key={a.allowance_id} className="ctl-form ctl-detail" aria-label={`Decision for comp ${shortId(a.allowance_id)}`}
                onSubmit={e => { e.preventDefault(); decideComp(a); }}>
                <h3>Decide comp {shortId(a.allowance_id)}: {money(a.amount, a.currency)}</h3>
                <fieldset disabled={busy}>
                  <div className="ctl-fields">
                    <Field label="Decision" options={DECISIONS} value={decision.decision} onChange={e => setDecision({ ...decision, decision: e.target.value })} />
                    <Field label="Decision note" type="text" value={decision.note} onChange={e => setDecision({ ...decision, note: e.target.value })} />
                  </div>
                  <div className="ctl-actions">
                    <button type="submit" className="ctl-primary"><Check size={16} />Confirm decision</button>
                    <button type="button" onClick={() => setDeciding(null)}>Cancel</button>
                  </div>
                </fieldset>
              </form>
            ))}
          </section>
        )}

        {tab === 'centres' && (
          <section role="tabpanel" aria-label="Cost centres">
            {canWrite && (
              <form className="ctl-form" aria-label="Create cost centre" onSubmit={e => { e.preventDefault(); createCentre(); }}>
                <h2>New cost centre</h2>
                <fieldset disabled={busy}>
                  <div className="ctl-fields">
                    <Field label="Cost centre name" type="text" maxLength={120} value={centreForm.name} onChange={set(setCentreForm, centreForm, 'name')} />
                    <Field label="Cost centre category" options={CATEGORIES} value={centreForm.category} onChange={set(setCentreForm, centreForm, 'category')} />
                    <Field label="Cost centre scope" options={CENTRE_SCOPES} value={centreForm.scope} onChange={set(setCentreForm, centreForm, 'scope')} />
                  </div>
                  <button type="submit" className="ctl-primary">Create cost centre</button>
                </fieldset>
              </form>
            )}
            {listLoading ? <p role="status">Loading cost centres...</p> : (
              <Table headers={['Name', 'Category', 'Site', 'Created']} label="Cost centres" empty={!centres.length} emptyText="No cost centres yet.">
                {centres.map(c => (
                  <tr key={c.cost_centre_id}>
                    <td>{c.name}</td>
                    <td>{statusLabel(c.category)}</td>
                    <td>{c.location_id ? shortId(c.location_id) : 'All sites'}</td>
                    <td>{formatWhen(c.created_at)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </section>
        )}

        {tab === 'consumables' && (
          <section role="tabpanel" aria-label="Consumables">
            {hints.length > 0 && (
              <div className="ctl-hints" role="status" aria-label="Variance hints">
                {hints.map(h => <p key={h}><AlertTriangle size={16} /> {h}</p>)}
              </div>
            )}
            {!canOperate && <p className="ctl-empty">You have read-only access to Controls. Issuing and counting need operator access.</p>}
            {canOperate && !centres.length && !listLoading && (
              <p className="ctl-empty">Create a cost centre first{canWrite ? '' : ' (ask a manager)'} to issue or count consumables against.</p>
            )}
            {canOperate && centres.length > 0 && <>
              <form className="ctl-form" aria-label="Issue consumable" onSubmit={e => { e.preventDefault(); issueConsumable(); }}>
                <h2>Issue consumables</h2>
                <fieldset disabled={busy}>
                  <div className="ctl-fields">
                    <Field label="Issue cost centre" options={centreOptions} value={issueForm.centre} onChange={set(setIssueForm, issueForm, 'centre')} />
                    <Field label="Issue item ID" type="text" value={issueForm.item} onChange={set(setIssueForm, issueForm, 'item')} />
                    <Field label="Issue quantity" {...decimalProps} value={issueForm.qty} onChange={set(setIssueForm, issueForm, 'qty')} />
                    <Field label="Issue unit" type="text" maxLength={16} value={issueForm.uom} onChange={set(setIssueForm, issueForm, 'uom')} />
                    <Field label="Issue notes" type="text" value={issueForm.notes} onChange={set(setIssueForm, issueForm, 'notes')} />
                  </div>
                  <button type="submit" className="ctl-primary" disabled={!hasSite}>Record issue</button>
                </fieldset>
              </form>
              <form className="ctl-form" aria-label="Count consumable" onSubmit={e => { e.preventDefault(); countConsumable(); }}>
                <h2>Count consumables</h2>
                <fieldset disabled={busy}>
                  <div className="ctl-fields">
                    <Field label="Count cost centre" options={centreOptions} value={countForm.centre} onChange={set(setCountForm, countForm, 'centre')} />
                    <Field label="Count item ID" type="text" value={countForm.item} onChange={set(setCountForm, countForm, 'item')} />
                    <Field label="Counted quantity" {...decimalProps} value={countForm.qty} onChange={set(setCountForm, countForm, 'qty')} />
                    <Field label="Count unit" type="text" maxLength={16} value={countForm.uom} onChange={set(setCountForm, countForm, 'uom')} />
                    <Field label="Count period" type="text" placeholder="YYYY-MM" maxLength={7} value={countForm.period} onChange={set(setCountForm, countForm, 'period')} />
                  </div>
                  <button type="submit" className="ctl-primary" disabled={!hasSite}>Record count</button>
                </fieldset>
              </form>
            </>}
          </section>
        )}

        {tab === 'exceptions' && (
          <section role="tabpanel" aria-label="Exceptions">
            <div className="ctl-filters">
              <Field label="Exception status" options={EXCEPTION_FILTERS} value={exceptionFilter} onChange={e => { setExceptionFilter(e.target.value); setResolving(null); }} />
            </div>
            {listLoading ? <p role="status">Loading exceptions...</p> : (
              <Table headers={['Raised', 'Kind', 'Exception', 'Status', 'Actions']} label="Exceptions" empty={!exceptions.length} emptyText="No exceptions match this filter.">
                {exceptions.map(x => (
                  <tr key={x.exception_id}>
                    <td>{formatWhen(x.created_at)}</td>
                    <td>{statusLabel(x.kind)}</td>
                    <td>
                      <strong>{x.title}</strong>
                      <div>{x.detail}</div>
                      {x.resolution_note && <div className="ctl-hint">Resolution: {x.resolution_note}</div>}
                    </td>
                    <td>{statusLabel(x.status)}</td>
                    <td className="ctl-row-actions">
                      {canWrite && x.status === 'open' && (
                        <button type="button" aria-label={`Acknowledge exception ${shortId(x.exception_id)}`} disabled={busy} onClick={() => acknowledge(x)}>Acknowledge</button>
                      )}
                      {canWrite && RESOLVABLE.includes(x.status) && (
                        <button type="button" aria-label={`Resolve exception ${shortId(x.exception_id)}`} disabled={busy}
                          onClick={() => { setResolving(x.exception_id); setResolveNote(''); }}>Resolve</button>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
            {canWrite && resolving && exceptions.filter(x => x.exception_id === resolving && RESOLVABLE.includes(x.status)).map(x => (
              <form key={x.exception_id} className="ctl-form ctl-detail" aria-label={`Resolution for exception ${shortId(x.exception_id)}`}
                onSubmit={e => { e.preventDefault(); resolve(x); }}>
                <h3>Resolve: {x.title}</h3>
                <fieldset disabled={busy}>
                  <div className="ctl-fields">
                    <Field label="Resolution note" type="text" maxLength={500} value={resolveNote} onChange={e => setResolveNote(e.target.value)} />
                  </div>
                  <div className="ctl-actions">
                    <button type="submit" className="ctl-primary"><Check size={16} />Confirm resolution</button>
                    <button type="button" onClick={() => setResolving(null)}>Cancel</button>
                  </div>
                </fieldset>
              </form>
            ))}
          </section>
        )}
      </>}
    </main>
  );
}
